import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Apple,
  Plus,
  Search,
  Calendar,
  Clock,
  User,
  TrendingUp,
  CheckCircle
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Animal, FeedingRecord } from '../LivestockManagementPage';

// Import design system tokens
import { 
  getCardClasses, 
  getShadowClasses, 
  getButtonClasses,
  components, 
  colors, 
  typography,
  spacing
} from '@/components/business/design-system/businessDesignTokens';

interface FeedingTabProps {
  animals: Animal[];
  feedingRecords: FeedingRecord[];
  setFeedingRecords: React.Dispatch<React.SetStateAction<FeedingRecord[]>>;
}

export function FeedingTab({ animals, feedingRecords, setFeedingRecords }: FeedingTabProps) {
  const { t } = useTranslation('business', { keyPrefix: 'livestock.feedingTab' });
  const { t: tc } = useTranslation('common');
  
  const [searchTerm, setSearchTerm] = useState('');
  const [animalFilter, setAnimalFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<FeedingRecord | null>(null);
  
  // Form state for adding feeding records
  const [formData, setFormData] = useState<Partial<FeedingRecord>>({
    animalId: '',
    date: new Date().toISOString().split('T')[0],
    time: new Date().toTimeString().slice(0, 5),
    foodType: '',
    quantity: 0,
    unit: 'kg',
    consumption: 'Full',
    notes: '',
    fedBy: ''
  });

  // Filter feeding records
  const filteredRecords = feedingRecords.filter(record => {
    const animal = animals.find(a => a.id === record.animalId);
    const matchesSearch = searchTerm === '' || 
      animal?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      record.foodType.toLowerCase().includes(searchTerm.toLowerCase()) ||
      record.fedBy.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesAnimal = animalFilter === 'all' || record.animalId === animalFilter;
    
    let matchesDate = true;
    if (dateFilter === 'today') {
      const today = new Date().toISOString().split('T')[0];
      matchesDate = record.date === today;
    } else if (dateFilter === 'week') {
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      matchesDate = new Date(record.date) >= weekAgo;
    }
    
    return matchesSearch && matchesAnimal && matchesDate;
  });

  // Feeding statistics
  const feedingStats = {
    totalFeedings: feedingRecords.length,
    todayFeedings: feedingRecords.filter(r => 
      r.date === new Date().toISOString().split('T')[0]
    ).length,
    fullConsumption: feedingRecords.filter(r => r.consumption === 'Full').length,
    partialConsumption: feedingRecords.filter(r => r.consumption === 'Partial').length,
    refused: feedingRecords.filter(r => r.consumption === 'Refused').length
  };

  const handleFormChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveRecord = () => {
    if (!formData.animalId || !formData.foodType || !formData.fedBy) {
      alert('Please fill in required fields');
      return;
    }

    const newRecord: FeedingRecord = {
      ...formData,
      id: `f${Date.now()}`,
      quantity: formData.quantity || 0
    } as FeedingRecord;
    
    setFeedingRecords(prev => [...prev, newRecord]);

    // Reset form
    setFormData({
      animalId: '',
      date: new Date().toISOString().split('T')[0],
      time: new Date().toTimeString().slice(0, 5),
      foodType: '',
      quantity: 0,
      unit: 'kg',
      consumption: 'Full',
      notes: '',
      fedBy: ''
    });
    setShowAddDialog(false);
  };

  const getAnimalName = (animalId: string) => {
    const animal = animals.find(a => a.id === animalId);
    return animal ? animal.name : 'Unknown Animal';
  };

  const getConsumptionColor = (consumption: FeedingRecord['consumption']) => {
    switch (consumption) {
      case 'Full': return 'default';
      case 'Partial': return 'secondary';
      case 'Refused': return 'destructive';
      default: return 'secondary';
    }
  };

  // Get today's feeding schedule
  const todayFeedings = feedingRecords.filter(r => 
    r.date === new Date().toISOString().split('T')[0]
  ).sort((a, b) => a.time.localeCompare(b.time));

  return (
    <div className={spacing.sectionGap}>
      {/* Streamlined Header */}
      <div className="mb-6">
        <p className="text-sm text-slate-600">{t('description')}</p>
      </div>

      {/* Streamlined Stats and Actions */}
      <Card className={getCardClasses('basic')}>
        <CardContent className="p-4">
          {/* Compact Stats Row */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-4 text-sm">
                <div className="text-center">
                  <div className="text-lg font-bold text-slate-900">{feedingStats.totalFeedings}</div>
                  <div className="text-xs text-slate-600">Total</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-blue-600">{feedingStats.todayFeedings}</div>
                  <div className="text-xs text-slate-600">Today</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-emerald-600">{feedingStats.fullConsumption}</div>
                  <div className="text-xs text-slate-600">Full Consumption</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-emerald-600">
                    {feedingRecords.length > 0 
                      ? Math.round((feedingStats.fullConsumption / feedingRecords.length) * 100)
                      : 0
                    }%
                  </div>
                  <div className="text-xs text-slate-600">Success Rate</div>
                </div>
              </div>
            </div>
            <Button onClick={() => setShowAddDialog(true)} className="bg-emerald-600 hover:bg-emerald-700">
              <Plus className="w-4 h-4 mr-2" />
              Log Feeding
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Compact Search and Filters */}
      <div className="space-y-4">
        {/* Compact Filters Row */}
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
          <div className="flex flex-1 gap-3 w-full sm:w-auto">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search by animal name or food type..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <div className="flex gap-2">
              <Select value={animalFilter} onValueChange={setAnimalFilter}>
                <SelectTrigger className="w-32">
                  <SelectValue placeholder="Animal" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Animals</SelectItem>
                  {animals.map(animal => (
                    <SelectItem key={animal.id} value={animal.id}>{animal.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={dateFilter} onValueChange={setDateFilter}>
                <SelectTrigger className="w-28">
                  <SelectValue placeholder="Date" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Dates</SelectItem>
                  <SelectItem value="today">Today</SelectItem>
                  <SelectItem value="week">This Week</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Results Count and Clear Filters */}
        <div className="flex items-center justify-between text-sm py-2 border-t border-slate-100">
          <span className="text-slate-600">
            Showing {filteredRecords.length} of {feedingRecords.length} feeding records
          </span>
          {(searchTerm || animalFilter !== 'all' || dateFilter !== 'all') && (
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => {
                setSearchTerm('');
                setAnimalFilter('all');
                setDateFilter('all');
              }}
              className="text-slate-500 hover:text-slate-700"
            >
              Clear Filters
            </Button>
          )}
        </div>
      </div>

      {/* Today's Feeding Schedule */}
      {todayFeedings.length > 0 && (
        <Card className="border-blue-200 bg-blue-50/50">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2 text-blue-900">
              <Clock className="w-5 h-5" />
              <span>Today's Feeding Schedule</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {todayFeedings.map(feeding => (
                <div key={feeding.id} className="bg-white p-3 rounded-lg border">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <p className="font-medium text-slate-900">{getAnimalName(feeding.animalId)}</p>
                      <p className="text-sm text-slate-600">{feeding.foodType}</p>
                    </div>
                    <Badge variant={getConsumptionColor(feeding.consumption)}>
                      {feeding.consumption}
                    </Badge>
                  </div>
                  <div className="text-xs text-slate-500 space-y-1">
                    <div>Time: {feeding.time}</div>
                    <div>Quantity: {feeding.quantity} {feeding.unit}</div>
                    <div>Fed by: {feeding.fedBy}</div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Feeding Records */}
      <Card className={getCardClasses('basic')}>
        <CardHeader>
          <CardTitle>Feeding Records</CardTitle>
          <CardDescription>
            Showing {filteredRecords.length} of {feedingRecords.length} records
          </CardDescription>
        </CardHeader>
        <CardContent>
          {filteredRecords.length === 0 ? (
            <div className="text-center py-8">
              <Apple className="h-12 w-12 mx-auto text-slate-400 mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">No feeding records found</h3>
              <p className="text-slate-600 mb-4">
                {feedingRecords.length === 0 
                  ? "Start by logging your first feeding"
                  : "Try adjusting your search criteria"
                }
              </p>
              <Button onClick={() => setShowAddDialog(true)} className="bg-emerald-600 hover:bg-emerald-700">
                <Plus className="w-4 h-4 mr-2" />
                Log First Feeding
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredRecords
                .sort((a, b) => {
                  const dateCompare = new Date(b.date).getTime() - new Date(a.date).getTime();
                  if (dateCompare !== 0) return dateCompare;
                  return b.time.localeCompare(a.time);
                })
                .map(record => (
                  <div 
                    key={record.id} 
                    className="border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => {
                      setSelectedRecord(record);
                      setShowViewDialog(true);
                    }}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-center space-x-3">
                        <h3 className="font-medium text-slate-900">
                          {getAnimalName(record.animalId)}
                        </h3>
                        <Badge variant={getConsumptionColor(record.consumption)}>
                          {record.consumption}
                        </Badge>
                      </div>
                      <div className="text-sm text-slate-500">
                        {new Date(record.date).toLocaleDateString()} at {record.time}
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-slate-600 mb-2">
                      <div>
                        <span className="font-medium">Food:</span> {record.foodType}
                      </div>
                      <div>
                        <span className="font-medium">Quantity:</span> {record.quantity} {record.unit}
                      </div>
                      <div>
                        <span className="font-medium">Fed by:</span> {record.fedBy}
                      </div>
                      <div>
                        <span className="font-medium">Consumption:</span> {record.consumption}
                      </div>
                    </div>
                    
                    {record.notes && (
                      <div className="text-sm text-slate-700 bg-slate-50 p-2 rounded">
                        <span className="font-medium">Notes:</span> {record.notes}
                      </div>
                    )}
                  </div>
                ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Feeding Record Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Log Feeding Record</DialogTitle>
            <DialogDescription>
              Record details about an animal feeding session
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="animalId">Animal *</Label>
                <Select value={formData.animalId} onValueChange={(value) => handleFormChange('animalId', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select animal" />
                  </SelectTrigger>
                  <SelectContent>
                    {animals.map(animal => (
                      <SelectItem key={animal.id} value={animal.id}>
                        {animal.name} ({animal.species})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="fedBy">Fed By *</Label>
                <Input
                  id="fedBy"
                  value={formData.fedBy || ''}
                  onChange={(e) => handleFormChange('fedBy', e.target.value)}
                  placeholder="Staff member name"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="date">Date *</Label>
                <Input
                  id="date"
                  type="date"
                  value={formData.date || ''}
                  onChange={(e) => handleFormChange('date', e.target.value)}
                />
              </div>
              
              <div>
                <Label htmlFor="time">Time *</Label>
                <Input
                  id="time"
                  type="time"
                  value={formData.time || ''}
                  onChange={(e) => handleFormChange('time', e.target.value)}
                />
              </div>
              
              <div>
                <Label htmlFor="consumption">Consumption *</Label>
                <Select value={formData.consumption} onValueChange={(value) => handleFormChange('consumption', value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Full">Full</SelectItem>
                    <SelectItem value="Partial">Partial</SelectItem>
                    <SelectItem value="Refused">Refused</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="foodType">Food Type *</Label>
              <Input
                id="foodType"
                value={formData.foodType || ''}
                onChange={(e) => handleFormChange('foodType', e.target.value)}
                placeholder="e.g., Hay, Fresh browse, Meat, Fruits"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="quantity">Quantity *</Label>
                <Input
                  id="quantity"
                  type="number"
                  value={formData.quantity || ''}
                  onChange={(e) => handleFormChange('quantity', parseFloat(e.target.value) || 0)}
                  placeholder="Amount given"
                />
              </div>
              
              <div>
                <Label htmlFor="unit">Unit</Label>
                <Select value={formData.unit} onValueChange={(value) => handleFormChange('unit', value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="kg">Kilograms (kg)</SelectItem>
                    <SelectItem value="g">Grams (g)</SelectItem>
                    <SelectItem value="lbs">Pounds (lbs)</SelectItem>
                    <SelectItem value="portions">Portions</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes || ''}
                onChange={(e) => handleFormChange('notes', e.target.value)}
                placeholder="Any observations about the feeding..."
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveRecord} className="bg-emerald-600 hover:bg-emerald-700">
              Save Record
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Record Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <Apple className="w-5 h-5 text-emerald-600" />
              <span>Feeding Record Details</span>
            </DialogTitle>
          </DialogHeader>

          {selectedRecord && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h3 className="font-medium text-slate-900 mb-2">Basic Information</h3>
                  <div className="space-y-2 text-sm">
                    <div><span className="text-slate-600">Animal:</span> {getAnimalName(selectedRecord.animalId)}</div>
                    <div><span className="text-slate-600">Date:</span> {new Date(selectedRecord.date).toLocaleDateString()}</div>
                    <div><span className="text-slate-600">Time:</span> {selectedRecord.time}</div>
                    <div><span className="text-slate-600">Fed by:</span> {selectedRecord.fedBy}</div>
                  </div>
                </div>
                <div>
                  <h3 className="font-medium text-slate-900 mb-2">Feeding Details</h3>
                  <div className="space-y-2 text-sm">
                    <div><span className="text-slate-600">Food Type:</span> {selectedRecord.foodType}</div>
                    <div><span className="text-slate-600">Quantity:</span> {selectedRecord.quantity} {selectedRecord.unit}</div>
                    <div>
                      <span className="text-slate-600">Consumption:</span>{' '}
                      <Badge variant={getConsumptionColor(selectedRecord.consumption)}>
                        {selectedRecord.consumption}
                      </Badge>
                    </div>
                  </div>
                </div>
              </div>

              {selectedRecord.notes && (
                <div>
                  <h3 className="font-medium text-slate-900 mb-2">Notes</h3>
                  <p className="text-sm text-slate-700 bg-slate-50 p-3 rounded-md">
                    {selectedRecord.notes}
                  </p>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowViewDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
} 